"use client";

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useContext,
  useState,
  useSyncExternalStore,
  useTransition,
} from "react";
import { saveMtbGroundTruth } from "@/app/actions/mtb-ground-truth";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import type { GroundTruthReviewWindow } from "@/lib/conditions/ground-truth-summary";
import {
  MTB_SURFACE_CONDITIONS,
  type MtbGroundTruthInput,
  type MtbGroundTruthPrefill,
  type MtbLiveScopeSummary,
  type MtbSurfaceCondition,
} from "@/lib/conditions/mtb-ground-truth";
import type { MtbGroundTruthPayload } from "@/lib/conditions/mtb-ground-truth-read";
import type { MtbRecentConditionPrefill } from "@/lib/companion/mtb-ground-truth-prefill";

export type MtbGroundTruthPanelProps = {
  locationSlug: string;
  locationName: string;
  estimatedCondition: string | null;
  windows: GroundTruthReviewWindow[];
  isLoggedIn: boolean;
  prefill?: MtbGroundTruthPrefill | null;
  recentConditionPrefill?: MtbRecentConditionPrefill | null;
};

type MtbGroundTruthFormState = {
  surfaceCondition: MtbSurfaceCondition | null;
  scopeKind: "park" | "zone" | "trail";
  zoneId: string;
  trailId: string;
  observedScore: number | null;
  possibleClosure: boolean;
  windowId: string;
  trailQuery: string;
};

type MtbGroundTruthFormAction =
  | { type: "surface"; value: MtbSurfaceCondition }
  | { type: "scope"; value: MtbGroundTruthFormState["scopeKind"] }
  | { type: "zone"; value: string }
  | { type: "trail-query"; value: string }
  | { type: "trail"; id: string; name: string }
  | { type: "score"; value: number | null }
  | { type: "closure"; value: boolean }
  | { type: "window"; value: string }
  | { type: "use-suggested-trail"; trailId: string }
  | { type: "submission-succeeded" };

const POLL_BASE_MS = 5 * 60_000;
const POLL_MAX_MS = 20 * 60_000;
const SCORE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
const SURFACE_LABELS: Record<MtbSurfaceCondition, string> = {
  dusty: "Dusty",
  tacky: "Tacky",
  loose: "Loose",
  damp: "Damp",
  muddy: "Muddy",
  "standing-water": "Standing water",
  "snow-or-ice": "Snow or ice",
};

type MtbGroundTruthLiveContextValue = {
  payload: MtbGroundTruthPayload | null;
  setPayload: Dispatch<SetStateAction<MtbGroundTruthPayload | null>>;
  refresh: () => void;
};

const MtbGroundTruthLiveContext = createContext<MtbGroundTruthLiveContextValue | null>(null);

export function initialMtbGroundTruthFormState(
  windows: GroundTruthReviewWindow[],
  prefill: MtbGroundTruthPrefill | null,
  recentConditionPrefill: MtbRecentConditionPrefill | null = null,
): MtbGroundTruthFormState {
  const prefilledWindow = prefill
    ? windows.find((window) => (
        window.reviewedStartAt === prefill.reviewedStartAt
        && window.reviewedEndAt === prefill.reviewedEndAt
      ))
    : null;
  return {
    surfaceCondition: recentConditionPrefill?.surfaceCondition ?? null,
    scopeKind: recentConditionPrefill ? "trail" : "park",
    zoneId: "",
    trailId: recentConditionPrefill?.trailId ?? "",
    observedScore: null,
    possibleClosure: false,
    windowId: prefilledWindow?.id ?? (prefill ? "tracked-session" : windows[0]?.id ?? ""),
    trailQuery: recentConditionPrefill ? "Selected trail" : "",
  };
}

export function mtbGroundTruthFormReducer(
  state: MtbGroundTruthFormState,
  action: MtbGroundTruthFormAction,
): MtbGroundTruthFormState {
  switch (action.type) {
    case "surface": return { ...state, surfaceCondition: action.value };
    case "scope": return { ...state, scopeKind: action.value };
    case "zone": return { ...state, zoneId: action.value };
    case "trail-query": return { ...state, trailQuery: action.value, trailId: "" };
    case "trail": return { ...state, trailId: action.id, trailQuery: action.name };
    case "score": return { ...state, observedScore: action.value };
    case "closure": return { ...state, possibleClosure: action.value };
    case "window": return { ...state, windowId: action.value };
    case "use-suggested-trail": return {
      ...state,
      scopeKind: "trail",
      trailId: action.trailId,
      trailQuery: "Suggested tracked trail",
    };
    case "submission-succeeded": return {
      ...state,
      surfaceCondition: null,
      scopeKind: "park",
      zoneId: "",
      trailId: "",
      observedScore: null,
      possibleClosure: false,
      trailQuery: "",
    };
  }
}

type MtbGroundTruthFormListener = () => void;

export class MtbGroundTruthFormController {
  private readonly listeners = new Set<MtbGroundTruthFormListener>();
  private snapshot: MtbGroundTruthFormState;
  private recentConditionPrefillKey: string;

  constructor(
    windows: GroundTruthReviewWindow[],
    prefill: MtbGroundTruthPrefill | null,
    recentConditionPrefill: MtbRecentConditionPrefill | null,
  ) {
    this.snapshot = initialMtbGroundTruthFormState(
      windows,
      prefill,
      recentConditionPrefill,
    );
    this.recentConditionPrefillKey = mtbRecentConditionPrefillKey(
      recentConditionPrefill,
    );
  }

  subscribe = (listener: MtbGroundTruthFormListener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.snapshot;

  dispatch = (action: MtbGroundTruthFormAction) => {
    this.publish(mtbGroundTruthFormReducer(this.snapshot, action));
  };

  synchroniseRecentConditionPrefill(
    windows: GroundTruthReviewWindow[],
    prefill: MtbGroundTruthPrefill | null,
    recentConditionPrefill: MtbRecentConditionPrefill | null,
  ): boolean {
    const nextKey = mtbRecentConditionPrefillKey(recentConditionPrefill);
    if (nextKey === this.recentConditionPrefillKey) return false;
    this.recentConditionPrefillKey = nextKey;
    this.publish(initialMtbGroundTruthFormState(windows, prefill, recentConditionPrefill));
    return true;
  }

  private publish(next: MtbGroundTruthFormState) {
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }
}

function mtbRecentConditionPrefillKey(
  prefill: MtbRecentConditionPrefill | null,
): string {
  return prefill === null
    ? "none"
    : `${prefill.trailId}\u0000${prefill.surfaceCondition ?? "busy"}`;
}

export function mtbGroundTruthPollDelayMs(failureCount: number): number {
  if (failureCount <= 0) return POLL_BASE_MS;
  return Math.min(POLL_BASE_MS * 2 ** failureCount, POLL_MAX_MS);
}

export function shouldPollMtbGroundTruth(enabled: boolean): boolean {
  return enabled;
}

export function MtbGroundTruthLocationProvider({
  locationSlug,
  enabled = true,
  children,
}: {
  locationSlug: string;
  enabled?: boolean;
  children?: ReactNode;
}) {
  const [payload, setPayload] = useState<MtbGroundTruthPayload | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    if (!shouldPollMtbGroundTruth(enabled)) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failureCount = 0;

    const schedule = (delayMs: number) => {
      if (timer !== null) clearTimeout(timer);
      if (!cancelled && !document.hidden) timer = setTimeout(load, delayMs);
    };
    const load = async () => {
      if (cancelled || document.hidden) return;
      try {
        const response = await fetch(
          `/api/conditions/${encodeURIComponent(locationSlug)}/mtb-ground-truth`,
          { cache: "no-store" },
        );
        if (!response.ok) throw new Error("MTB reports unavailable");
        const nextPayload = await response.json() as MtbGroundTruthPayload;
        if (cancelled) return;
        setPayload(nextPayload);
        failureCount = 0;
        schedule(mtbGroundTruthPollDelayMs(failureCount));
      } catch {
        if (cancelled) return;
        failureCount += 1;
        schedule(mtbGroundTruthPollDelayMs(failureCount));
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (timer !== null) clearTimeout(timer);
        timer = null;
      } else {
        void load();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    void load();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, locationSlug, refreshVersion]);

  return (
    <MtbGroundTruthLiveContext.Provider value={{
      payload,
      setPayload,
      refresh: () => setRefreshVersion((value) => value + 1),
    }}>
      {children}
    </MtbGroundTruthLiveContext.Provider>
  );
}

function useMtbGroundTruthLive(): MtbGroundTruthLiveContextValue {
  const value = useContext(MtbGroundTruthLiveContext);
  if (value === null) throw new Error("MTB ground-truth views require MtbGroundTruthLocationProvider.");
  return value;
}

export function MtbGroundTruthSpotPanel(
  props: Omit<MtbGroundTruthPanelProps, "isLoggedIn">,
) {
  const { isLoggedIn } = useSpotUser();
  return <MtbGroundTruthPanel {...props} isLoggedIn={isLoggedIn} />;
}

export function MtbGroundTruthPanel({
  ...props
}: MtbGroundTruthPanelProps) {
  return (
    <MtbGroundTruthLocationProvider locationSlug={props.locationSlug}>
      <MtbGroundTruthPanelContent {...props} mode="combined" />
    </MtbGroundTruthLocationProvider>
  );
}

export function MtbGroundTruthReportPanel(
  props: Omit<MtbGroundTruthPanelProps, "isLoggedIn">,
) {
  const { isLoggedIn } = useSpotUser();
  return <MtbGroundTruthPanelContent {...props} isLoggedIn={isLoggedIn} mode="report" />;
}

export function MtbGroundTruthForecastPanel({
  locationName,
  estimatedCondition,
}: Pick<MtbGroundTruthPanelProps, "locationName" | "estimatedCondition">) {
  const { payload } = useMtbGroundTruthLive();
  return (
    <section aria-labelledby="mtb-live-rider-title" className="rounded-[18px] border border-border-light bg-white p-5 md:p-6">
      <p className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">Live rider check</p>
      <h3 id="mtb-live-rider-title" className="mt-1 text-xl font-semibold text-navy">
        {locationName} surface reports
      </h3>
      <p className="mt-1 text-[12.5px] leading-relaxed text-text-muted">
        Recent rider evidence can change today&apos;s decision but never changes the PeakWave score.
      </p>
      <div className="mt-5">
        <MtbGroundTruthLiveCard estimatedCondition={estimatedCondition} payload={payload} now={new Date()} />
      </div>
    </section>
  );
}

function MtbGroundTruthPanelContent({
  locationSlug,
  locationName,
  estimatedCondition,
  windows,
  isLoggedIn,
  prefill = null,
  recentConditionPrefill = null,
  mode,
}: MtbGroundTruthPanelProps & { mode: "combined" | "report" }) {
  const { payload, setPayload, refresh } = useMtbGroundTruthLive();
  const [formController] = useState(() => new MtbGroundTruthFormController(
    windows,
    prefill,
    recentConditionPrefill,
  ));
  const form = useSyncExternalStore(
    formController.subscribe,
    formController.getSnapshot,
    formController.getSnapshot,
  );
  const dispatch = formController.dispatch;
  const [status, setStatus] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const statusMessage = status ?? (isLoggedIn
    ? "Share what the trail surface is actually like."
    : "Sign in to report conditions.");

  useEffect(() => {
    formController.synchroniseRecentConditionPrefill(
      windows,
      prefill,
      recentConditionPrefill,
    );
  }, [formController, prefill, recentConditionPrefill, windows]);

  useEffect(() => {
    const query = form.trailQuery.trim();
    if (form.scopeKind !== "trail" || query.length < 2 || form.trailId) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/conditions/${encodeURIComponent(locationSlug)}/mtb-ground-truth?trailQuery=${encodeURIComponent(query.slice(0, 80))}`,
          { cache: "no-store", signal: controller.signal },
        );
        if (!response.ok) return;
        const nextPayload = await response.json() as MtbGroundTruthPayload;
        setPayload(nextPayload);
      } catch {
        // Optional trail search should not block park or zone reports.
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.scopeKind, form.trailId, form.trailQuery, locationSlug, setPayload]);

  const selectedWindow = windows.find((window) => window.id === form.windowId) ?? null;
  const hasValidScope = form.scopeKind === "park"
    || (form.scopeKind === "zone" && form.zoneId.length > 0)
    || (form.scopeKind === "trail" && form.trailId.length > 0);
  const canSubmit = form.surfaceCondition !== null && hasValidScope && !pending;

  return (
    <section
      aria-labelledby="mtb-ground-truth-title"
      className="rounded-[18px] border border-border-light bg-white p-5 md:p-6"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted">
            On the ground
          </p>
          <h3 id="mtb-ground-truth-title" className="mt-1 text-xl font-semibold text-navy">
            {locationName} trail conditions
          </h3>
        </div>
        <span className="w-fit rounded-full bg-gold/15 px-3 py-1.5 text-[12px] font-semibold text-navy">
          Rider reports
        </span>
      </div>

      {mode === "combined" ? (
        <div className="mt-5">
          <MtbGroundTruthLiveCard
            estimatedCondition={estimatedCondition}
            payload={payload}
            now={new Date()}
          />
        </div>
      ) : null}

      <div className={mode === "combined" ? "mt-5 border-t border-border-light pt-5" : "mt-5"}>
        <h4 className="text-[15px] font-semibold text-navy">Report conditions</h4>
        <p className="mt-1 text-[12.5px] leading-relaxed text-text-muted">
          Rider reports do not change the live PeakWave score. They inform reviewed future calibration.
        </p>

        <div className="mt-5 space-y-5">
          <fieldset>
            <legend className="text-[12px] font-semibold text-text-muted">Trail surface</legend>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {MTB_SURFACE_CONDITIONS.map((condition) => (
                <button
                  key={condition}
                  type="button"
                  aria-pressed={form.surfaceCondition === condition}
                  disabled={pending}
                  onClick={() => dispatch({ type: "surface", value: condition })}
                  className={`min-h-[44px] rounded-sm border px-3 py-2 text-[12px] font-semibold transition-colors disabled:opacity-55 ${
                    form.surfaceCondition === condition
                      ? "border-navy bg-navy text-white"
                      : "border-border bg-warm-bg text-navy hover:border-gold/60"
                  }`}
                >
                  {SURFACE_LABELS[condition]}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-[12px] font-semibold text-text-muted">Where</legend>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {([
                ["park", "Whole park"],
                ["zone", "Zone"],
                ["trail", "Trail"],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={form.scopeKind === value}
                  disabled={pending}
                  onClick={() => dispatch({ type: "scope", value })}
                  className={`min-h-[44px] rounded-sm border px-2 py-2 text-[12px] font-semibold ${
                    form.scopeKind === value
                      ? "border-gold bg-gold text-navy"
                      : "border-border bg-warm-bg text-navy"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>

          {form.scopeKind === "zone" ? (
            <label className="block">
              <span className="mb-2 block text-[12px] font-semibold text-text-muted">
                Reporting zone
              </span>
              <select
                value={form.zoneId}
                onChange={(event) => dispatch({ type: "zone", value: event.target.value })}
                className="min-h-[44px] w-full rounded-sm border border-border bg-warm-bg px-3 text-[14px] text-navy"
              >
                <option value="">Choose a zone</option>
                {(payload?.zones ?? []).map((zone) => (
                  <option key={zone.id} value={zone.id}>{zone.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          {form.scopeKind === "trail" ? (
            <div>
              <label className="block">
                <span className="mb-2 block text-[12px] font-semibold text-text-muted">
                  Trail name
                </span>
                <input
                  value={form.trailQuery}
                  onChange={(event) => dispatch({ type: "trail-query", value: event.target.value })}
                  placeholder="Type at least 2 characters"
                  className="min-h-[44px] w-full rounded-sm border border-border bg-warm-bg px-3 text-[14px] text-navy"
                />
              </label>
              {!form.trailId && (payload?.trails.length ?? 0) > 0 ? (
                <div className="mt-2 grid gap-2">
                  {payload?.trails.slice(0, 20).map((trail) => (
                    <button
                      key={trail.id}
                      type="button"
                      onClick={() => dispatch({ type: "trail", id: trail.id, name: trail.name })}
                      className="min-h-[44px] rounded-sm border border-border bg-white px-3 py-2 text-left text-[13px] font-semibold text-navy"
                    >
                      {trail.name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {prefill?.suggestedTrailIds[0] ? (
            <div className="rounded-sm bg-teal/10 p-3">
              <p className="text-[12px] font-semibold text-navy">Tracked ride suggestion</p>
              <p className="mt-1 text-[12px] text-text-muted">
                You can change the scope before sending.
              </p>
              <button
                type="button"
                onClick={() => dispatch({
                  type: "use-suggested-trail",
                  trailId: prefill.suggestedTrailIds[0],
                })}
                className="mt-2 min-h-[44px] rounded-sm border border-teal/30 bg-white px-4 py-2 text-[12px] font-semibold text-navy"
              >
                Use suggested trail
              </button>
            </div>
          ) : null}

          <fieldset>
            <legend className="text-[12px] font-semibold text-text-muted">Optional score</legend>
            <div className="mt-2 grid grid-cols-6 gap-2 sm:grid-cols-11">
              <button
                type="button"
                aria-pressed={form.observedScore === null}
                onClick={() => dispatch({ type: "score", value: null })}
                className="min-h-[44px] rounded-sm border border-border bg-warm-bg px-2 text-[12px] font-semibold text-navy"
              >
                Skip
              </button>
              {SCORE_OPTIONS.map((score) => (
                <button
                  key={score}
                  type="button"
                  aria-pressed={form.observedScore === score}
                  onClick={() => dispatch({ type: "score", value: score })}
                  className={`min-h-[44px] rounded-sm border px-2 text-[12px] font-semibold ${
                    form.observedScore === score
                      ? "border-gold bg-gold text-navy"
                      : "border-border bg-warm-bg text-navy"
                  }`}
                >
                  {score}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="flex min-h-[44px] items-center gap-3 rounded-sm border border-border bg-warm-bg px-3 py-2">
            <input
              type="checkbox"
              checked={form.possibleClosure}
              onChange={(event) => dispatch({ type: "closure", value: event.target.checked })}
              className="h-5 w-5 accent-navy"
            />
            <span className="text-[13px] font-semibold text-navy">Possible closure</span>
          </label>

          <label className="block">
            <span className="mb-2 block text-[12px] font-semibold text-text-muted">
              Window reviewed
            </span>
            <select
              value={form.windowId}
              disabled={windows.length === 0 && !prefill}
              onChange={(event) => dispatch({ type: "window", value: event.target.value })}
              className="min-h-[44px] w-full rounded-sm border border-border bg-warm-bg px-3 text-[14px] text-navy disabled:opacity-55"
            >
              {prefill && !windows.some((window) => (
                window.reviewedStartAt === prefill.reviewedStartAt
                && window.reviewedEndAt === prefill.reviewedEndAt
              )) ? <option value="tracked-session">Tracked session</option> : null}
              {windows.length === 0 && !prefill ? <option value="">Just now</option> : null}
              {windows.map((window) => (
                <option key={window.id} value={window.id}>{window.label}</option>
              ))}
            </select>
          </label>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p role="status" className="text-[12.5px] leading-relaxed text-text-muted">
              {statusMessage}
            </p>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => {
                if (!isLoggedIn) {
                  setStatus("Sign in to report conditions.");
                  return;
                }
                const surfaceCondition = form.surfaceCondition;
                if (surfaceCondition === null) return;
                const input = buildMtbGroundTruthSubmissionInput({
                  locationSlug,
                  form: { ...form, surfaceCondition },
                  selectedWindow,
                  prefill,
                });
                startTransition(async () => {
                  const result = await saveMtbGroundTruth(input);
                  if (result.ok || (!result.ok && result.reportSaved)) {
                    dispatch({ type: "submission-succeeded" });
                    refresh();
                  }
                  setStatus(result.ok
                    ? "Report saved. Thank you for helping PeakWave learn."
                    : result.error);
                });
              }}
              className="min-h-[44px] shrink-0 rounded-sm bg-navy px-5 py-2 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pending ? "Sending" : "Send report"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function MtbGroundTruthLiveCard({
  estimatedCondition,
  payload,
  now,
}: {
  estimatedCondition: string | null;
  payload: MtbGroundTruthPayload | null;
  now: Date;
}) {
  const park = payload?.live.park ?? null;
  const closureCount = payload === null
    ? 0
    : [payload.live.park, ...payload.live.zones, ...payload.live.trails]
        .reduce((total, scope) => total + (scope?.possibleClosureCount ?? 0), 0);

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="rounded-sm bg-navy px-4 py-4 text-white">
        <p className="text-[11px] font-semibold uppercase tracking-[1px] text-white/65">
          Model estimate
        </p>
        <p className="mt-1 text-[15px] font-semibold">
          {estimatedCondition
            ? `PeakWave estimate: ${estimatedCondition}`
            : "PeakWave estimate unavailable"}
        </p>
      </div>
      <div className="rounded-sm border border-border bg-warm-bg px-4 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">
          Recent rider reports
        </p>
        {park ? (
          <p className="mt-1 text-[14px] font-semibold text-navy">
            {liveScopeCopy(park, now)}
          </p>
        ) : (
          <p className="mt-1 text-[13px] text-text-muted">No recent rider reports.</p>
        )}
        {closureCount > 0 ? (
          <p className="mt-2 text-[12px] font-semibold text-terracotta">
            Possible closure reported, check official status.
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function buildMtbGroundTruthSubmissionInput({
  locationSlug,
  form,
  selectedWindow,
  prefill,
}: {
  locationSlug: string;
  form: MtbGroundTruthFormState & { surfaceCondition: MtbSurfaceCondition };
  selectedWindow: GroundTruthReviewWindow | null;
  prefill: MtbGroundTruthPrefill | null;
}): MtbGroundTruthInput {
  const usesTrackedWindow = prefill !== null && (
    form.windowId === "tracked-session"
    || (
      selectedWindow?.reviewedStartAt === prefill.reviewedStartAt
      && selectedWindow.reviewedEndAt === prefill.reviewedEndAt
    )
  );
  const scope = form.scopeKind === "zone"
    ? { kind: "zone" as const, zoneId: form.zoneId }
    : form.scopeKind === "trail"
      ? { kind: "trail" as const, trailId: form.trailId }
      : { kind: "park" as const };
  return {
    locationSlug,
    surfaceCondition: form.surfaceCondition,
    scope,
    observedScore: form.observedScore,
    possibleClosure: form.possibleClosure,
    reviewedStartAt: usesTrackedWindow
      ? prefill.reviewedStartAt
      : selectedWindow?.reviewedStartAt ?? null,
    reviewedEndAt: usesTrackedWindow
      ? prefill.reviewedEndAt
      : selectedWindow?.reviewedEndAt ?? null,
    trackedSessionId: usesTrackedWindow ? prefill.trackedSessionId : null,
  };
}

function liveScopeCopy(scope: MtbLiveScopeSummary, now: Date): string {
  const updated = `updated ${relativeMinutes(scope.latestObservedAt, now)}`;
  if (scope.mixed) {
    const counts = scope.conditions
      .map((condition) => `${condition.reportCount} ${surfaceLabel(condition.condition)}`)
      .join(" · ");
    return `Mixed reports${counts ? ` · ${counts}` : ""} · ${updated}`;
  }
  if (scope.consensus) {
    return `Riders report: ${surfaceLabel(scope.consensus)} · ${scope.reportCount} recent reports · ${updated}`;
  }
  if (scope.observation) {
    return `1 rider reported ${surfaceLabel(scope.observation)} · ${updated}`;
  }
  return `${scope.reportCount} recent rider reports · ${updated}`;
}

function surfaceLabel(condition: MtbSurfaceCondition): string {
  return SURFACE_LABELS[condition];
}

function relativeMinutes(value: string, now: Date): string {
  const elapsedMinutes = Math.max(0, Math.round((now.getTime() - Date.parse(value)) / 60_000));
  if (elapsedMinutes < 1) return "just now";
  if (elapsedMinutes === 1) return "1 min ago";
  if (elapsedMinutes < 60) return `${elapsedMinutes} min ago`;
  const hours = Math.round(elapsedMinutes / 60);
  return hours === 1 ? "1 hr ago" : `${hours} hrs ago`;
}
